import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, GripVertical, Lock, Unlock, Calendar, X, ChevronDown, ChevronUp, Loader2, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { TipTapEditor } from '../../components/TipTapEditor';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { courseAPI, sectionAPI, chapterAPI, questionAPI, answerAPI, mediaAPI } from '../../lib/api';
 
interface Question {
  id: string;
  question: string;
  answers: Array<{ id?: string; text: string; isCorrect: boolean }>;
}

interface Chapter {
  id: string;
  title: string;
  subtitle: string;
  objectives: string[];
  content: string;
  media: any[];
  questions: Question[];
}

interface Section {
  id: string;
  title: string;
  chapters: Chapter[];
}

interface Course {
  id?: string;
  name: string;
  title: string;
  subtitle: string;
  coverImage: string | null;
  objectives: string[];
  timeline: string;
  startDate?: string | null;
  sections: Section[];
  status?: 'DRAFT' | 'PUBLISHED';
}

interface CourseBuilderProps {
  course?: Course;
  onSave: (course: Course) => void;
  onCancel: () => void;
}
function SortableSection({ section, index, onUpdate, onDelete }: any) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-3 mb-4">
        <div {...attributes} {...listeners} className="cursor-move">
          <GripVertical className="w-5 h-5 text-gray-400" />
        </div>
        <Input
          placeholder="Section Title"
          value={section.title}
          onChange={(e) => onUpdate(section.id, 'title', e.target.value)}
          className="flex-1"
        />
        <Button variant="ghost" size="icon" onClick={() => onDelete(section.id)}>
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      </div>
      {section.chapters.length === 0 && (
        <p className="text-sm text-gray-500 italic">No chapters yet. Add a chapter to get started.</p>
      )}
    </div>
  );
}

export function CourseBuilder({ course, onSave, onCancel }: CourseBuilderProps) {
  const [formData, setFormData] = useState<Course>(
    course || {
      name: '',
      title: '',
      subtitle: '',
      coverImage: null,
      objectives: [''],
      timeline: '',
      startDate: '',
      sections: [],
    }
  );

  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [activeChapter, setActiveChapter] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

   const navigate = useNavigate();
   const fetchCourseData = async (courseId: string) => {
    try {
      setLoading(true);
      const response = await courseAPI.getById(courseId);
      
      // ✅ Properly convert ISO date string to YYYY-MM-DD
      let formattedStartDate = '';
      if (response.data.startDate) {
        const date = new Date(response.data.startDate);
        formattedStartDate = date.toISOString().split('T')[0];
      }
      
      const courseData = {
        ...response.data,
        startDate: formattedStartDate,
        subtitle: response.data.subtitle || '',
        timeline: response.data.timeline || '',
      };
      
      setFormData(courseData);
    } catch (error: any) {
      console.error('Failed to fetch course:', error);
      
      // ✅ Check for session expiration (401)
      if (error.message?.includes('Session expired') || 
          error.message?.includes('401')) {
        // Clear token (already cleared in api.ts)
        // Redirect to admin login page
        navigate('/admin', { replace: true });
        return;
      }
      
      // Handle other errors
      alert(error.message || 'Failed to load course data');
    } finally {
      setLoading(false);
    }
  };

  // ✅ FETCH FULL COURSE DATA IF EDITING
  useEffect(() => {
    if (course?.id) {
      fetchCourseData(course.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?.id]); // Remove fetchCourseData from deps


  const updateField = (field: keyof Course, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const addObjective = () => {
    updateField('objectives', [...formData.objectives, '']);
  };

  const updateObjective = (index: number, value: string) => {
    const newObjectives = [...formData.objectives];
    newObjectives[index] = value;
    updateField('objectives', newObjectives);
  };

  const removeObjective = (index: number) => {
    updateField('objectives', formData.objectives.filter((_, i) => i !== index));
  };

  // ✅ ADD SECTION - DATABASE CALL
  const addSection = async () => {
    if (!formData.id) {
      // If course not saved yet, add locally
      const newSection: Section = {
        id: `temp-${Date.now()}`,
        title: '',
        chapters: [],
      };
      updateField('sections', [...formData.sections, newSection]);
      return;
    }

    try {
      const response = await sectionAPI.create(formData.id, { title: 'New Section' });
      const newSection = response.data;
      updateField('sections', [...formData.sections, { ...newSection, chapters: [] }]);
    } catch (error: any) {
      alert(error.message || 'Failed to create section');
    }
  };

 const updateSection = async (sectionId: string, field: string, value: any) => {
  if (field === "title" && !value.trim()) return; // ✅ FIX

  const newSections = formData.sections.map((s) =>
    s.id === sectionId ? { ...s, [field]: value } : s
  );
  updateField('sections', newSections);

  if (formData.id && !sectionId.startsWith('temp-')) {
    try {
      await sectionAPI.update(sectionId, { title: value });
    } catch (error: any) {
      console.error('Failed to update section:', error);
    }
  }
};

  // ✅ DELETE SECTION - DATABASE CALL
  const deleteSection = async (sectionId: string) => {
    if (!window.confirm('Delete section?')) return;

    // Remove from UI first
    updateField('sections', formData.sections.filter((s) => s.id !== sectionId));
    if (activeSection === sectionId) {
      setActiveSection(null);
      setActiveChapter(null);
    }

    // Call API if not temp
    if (formData.id && !sectionId.startsWith('temp-')) {
      try {
        await sectionAPI.delete(sectionId);
      } catch (error: any) {
        alert(error.message || 'Failed to delete section');
      }
    }
  };

  // ✅ ADD CHAPTER - DATABASE CALL (FIXED: sends questions)
  const addChapter = async (sectionId: string) => {
    const newChapter: Chapter = {
      id: `temp-${Date.now()}`,
      title: 'New Chapter',
      subtitle: '',
      objectives: [''],
      content: '',
      media: [],
      questions: [
        {
          id: `temp-q-${Date.now()}`,
          question: '',
          answers: [
            { text: '', isCorrect: true },
            { text: '', isCorrect: false },
          ],
        },
      ], // ✅ default question
    };

    // Add to UI first
    const newSections = formData.sections.map((s) =>
      s.id === sectionId ? { ...s, chapters: [...s.chapters, newChapter] } : s
    );
    updateField('sections', newSections);
    setActiveSection(sectionId);
    setActiveChapter(newChapter.id);
    toggleSection(sectionId);

    // Call API if section is saved
    if (formData.id && !sectionId.startsWith('temp-')) {
      try {
        const response = await chapterAPI.create(sectionId, {
          title: newChapter.title,
          subtitle: newChapter.subtitle,
          objectives: newChapter.objectives,
          content: newChapter.content,
          questions: newChapter.questions, // ✅ SEND QUESTIONS
        });

        // Replace temp ID with real ID
        const realChapter = response.data;
        const updatedSections = newSections.map((s) =>
          s.id === sectionId
            ? {
                ...s,
                chapters: s.chapters.map((c) =>
                  c.id === newChapter.id ? realChapter : c
                ),
              }
            : s
        );

        updateField('sections', updatedSections);
        setActiveChapter(realChapter.id);
      } catch (error: any) {
        alert(error.message || 'Failed to create chapter');
      }
    }
  };

  // ✅ UPDATE CHAPTER - DATABASE CALL (UNCHANGED)
  const updateChapter = async (
    sectionId: string,
    chapterId: string,
    field: keyof Chapter,
    value: any
  ) => {
    const newSections = formData.sections.map((s) =>
      s.id === sectionId
        ? {
            ...s,
            chapters: s.chapters.map((c) =>
              c.id === chapterId ? { ...c, [field]: value } : c
            ),
          }
        : s
    );
    updateField('sections', newSections);

    // Call API if not temp
    if (formData.id && !chapterId.startsWith('temp-')) {
      try {
        await chapterAPI.update(chapterId, { [field]: value });
      } catch (error: any) {
        console.error('Failed to update chapter:', error);
      }
    }
  };
  // ✅ DELETE CHAPTER - DATABASE CALL
  const deleteChapter = async (sectionId: string, chapterId: string) => {
    if (!window.confirm('Delete this chapter?')) return;

    const newSections = formData.sections.map((s) =>
      s.id === sectionId ? { ...s, chapters: s.chapters.filter((c) => c.id !== chapterId) } : s
    );
    updateField('sections', newSections);
    if (activeChapter === chapterId) {
      setActiveChapter(null);
    }

    // Call API if not temp
    if (formData.id && !chapterId.startsWith('temp-')) {
      try {
        await chapterAPI.delete(chapterId);
      } catch (error: any) {
        alert(error.message || 'Failed to delete chapter');
      }
    }
  };

  const addChapterObjective = (sectionId: string, chapterId: string) => {
    const section = formData.sections.find((s) => s.id === sectionId);
    const chapter = section?.chapters.find((c) => c.id === chapterId);
    if (chapter) {
      updateChapter(sectionId, chapterId, 'objectives', [...chapter.objectives, '']);
    }
  };

  const updateChapterObjective = (sectionId: string, chapterId: string, index: number, value: string) => {
    const section = formData.sections.find((s) => s.id === sectionId);
    const chapter = section?.chapters.find((c) => c.id === chapterId);
    if (chapter) {
      const newObjectives = [...chapter.objectives];
      newObjectives[index] = value;
      updateChapter(sectionId, chapterId, 'objectives', newObjectives);
    }
  };

  const removeChapterObjective = (sectionId: string, chapterId: string, index: number) => {
    const section = formData.sections.find((s) => s.id === sectionId);
    const chapter = section?.chapters.find((c) => c.id === chapterId);
    if (chapter) {
      updateChapter(sectionId, chapterId, 'objectives', chapter.objectives.filter((_, i) => i !== index));
    }
  };

    // ✅ ADD QUESTION (UI ONLY - FIXED)
  const addQuestion = async (sectionId: string, chapterId: string) => {
    const section = formData.sections.find((s) => s.id === sectionId);
    const chapter = section?.chapters.find((c) => c.id === chapterId);
    if (!chapter) return;

    const newQuestion: Question = {
      id: `temp-${Date.now()}`,
      question: '',
      answers: [
        { text: '', isCorrect: true },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
      ],
    };

    // ✅ ONLY update local state
    updateChapter(sectionId, chapterId, 'questions', [
      ...chapter.questions,
      newQuestion,
    ]);
  };
  const updateQuestion = async (sectionId: string, chapterId: string, questionId: string, field: string, value: any) => {
    const section = formData.sections.find((s) => s.id === sectionId);
    const chapter = section?.chapters.find((c) => c.id === chapterId);
    if (chapter) {
      const newQuestions = chapter.questions.map((q) =>
        q.id === questionId ? { ...q, [field]: value } : q
      );
      updateChapter(sectionId, chapterId, 'questions', newQuestions);

      // Call API if not temp
      if (formData.id && !questionId.startsWith('temp-')) {
        try {
          await questionAPI.update(questionId, { [field]: value });
        } catch (error: any) {
          console.error('Failed to update question:', error);
        }
      }
    }
  };

  const updateAnswer = (sectionId: string, chapterId: string, questionId: string, answerIndex: number, field: string, value: any) => {
    const section = formData.sections.find((s) => s.id === sectionId);
    const chapter = section?.chapters.find((c) => c.id === chapterId);
    const question = chapter?.questions.find((q) => q.id === questionId);
    if (question) {
      const newAnswers = question.answers.map((a, i) =>
        i === answerIndex ? { ...a, [field]: value } : a
      );
      updateQuestion(sectionId, chapterId, questionId, 'answers', newAnswers);
    }
  };

  const deleteQuestion = async (sectionId: string, chapterId: string, questionId: string) => {
    const section = formData.sections.find((s) => s.id === sectionId);
    const chapter = section?.chapters.find((c) => c.id === chapterId);
    if (chapter) {
      updateChapter(sectionId, chapterId, 'questions', chapter.questions.filter((q) => q.id !== questionId));

      // Call API if not temp
      if (formData.id && !questionId.startsWith('temp-')) {
        try {
          await questionAPI.delete(questionId);
        } catch (error: any) {
          console.error('Failed to delete question:', error);
        }
      }
    }
  };

  const handleCoverImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateField('coverImage', reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = formData.sections.findIndex((s) => s.id === active.id);
      const newIndex = formData.sections.findIndex((s) => s.id === over.id);

      const reordered = arrayMove(formData.sections, oldIndex, newIndex);
      updateField('sections', reordered);

      // Call reorder API
      if (formData.id) {
        try {
          await sectionAPI.reorder(
            formData.id,
            reordered.map((s) => s.id)
          );
        } catch (error: any) {
          console.error('Failed to reorder sections:', error);
        }
      }
    }
  };

  // ✅ SAVE COURSE - DATABASE CALL
  const handleSave = async (status: 'DRAFT' | 'PUBLISHED') => {
    try {
      setSaving(true);

      const courseData = {
        name: formData.name,
        title: formData.title,
        subtitle: formData.subtitle,
        coverImage: formData.coverImage,
        objectives: formData.objectives.filter(o => o.trim()),
        timeline: formData.timeline,
        startDate: formData.startDate ? new Date(formData.startDate) : null,
        status,
        sections: formData.sections,
      };

      let response;
      if (formData.id) {
        // Update existing course
        response = await courseAPI.update(formData.id, courseData);
      } else {
        // Create new course
        response = await courseAPI.create(courseData);
      }

      alert(`Course ${formData.id ? 'updated' : 'created'} successfully!`);
      onSave(response.data);
    } catch (error: any) {
      alert(error.message || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const getActiveChapterData = () => {
    if (!activeSection || !activeChapter) return null;
    const section = formData.sections.find((s) => s.id === activeSection);
    const chapter = section?.chapters.find((c) => c.id === activeChapter);
    return { section, chapter };
  };

  const activeData = getActiveChapterData();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading course data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Course Header */}
      <Card>
        <CardHeader>
          <CardTitle>Course Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Cover Image */}
          <div>
            <Label>Cover Image</Label>
            {formData.coverImage ? (
              <div className="relative mt-2">
                <img src={formData.coverImage} alt="Cover" className="w-full h-48 object-cover rounded-lg" />
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => updateField('coverImage', null)}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div className="mt-2">
                <Input type="file" accept="image/*" onChange={handleCoverImageUpload} />
              </div>
            )}
          </div>

          {/* Course Name */}
          <div>
            <Label>Course Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="e.g., Introduction to Programming"
            />
          </div>

          {/* Title & Subtitle */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Course Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Main course title"
              />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input
                value={formData.subtitle || ''}
                onChange={(e) => updateField('subtitle', e.target.value)}
                placeholder="Course subtitle"
              />
            </div>
          </div>

          {/* Learning Objectives */}
          <div>
            <Label>Learning Objectives</Label>
            <div className="space-y-2 mt-2">
              {formData.objectives.map((obj, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={obj}
                    onChange={(e) => updateObjective(index, e.target.value)}
                    placeholder={`Objective ${index + 1}`}
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeObjective(index)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addObjective}>
                <Plus className="w-4 h-4 mr-2" /> Add Objective
              </Button>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <Label>Timeline / Duration</Label>
            <Input
              value={formData.timeline}
              onChange={(e) => updateField('timeline', e.target.value)}
              placeholder="e.g., 8 weeks, 40 hours"
            />
          </div>
          {/* Start Date - Source of Truth for Locking */}
            <div>
              <Label>Course Start Date (Cohort Start)</Label>
              <div className="flex gap-2 mt-2 items-center">
                <Calendar className="w-5 h-5 text-gray-400" />
                <Input
                  type="date"
                   value={formData.startDate || ''}
                   onChange={(e) => updateField('startDate', e.target.value)}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Course will be locked until this date. Students cannot access before start date.
              </p>
            </div>

             
            </CardContent>
          </Card>

      {/* Sections Management */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Course Sections</CardTitle>
          <Button onClick={addSection}>
            <Plus className="w-4 h-4 mr-2" /> Add Section
          </Button>
        </CardHeader>
        <CardContent>
          {formData.sections.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No sections yet. Click "Add Section" to get started.</p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={formData.sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-4">
                  {formData.sections.map((section, index) => (
                    <div key={section.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <SortableSection
                            section={section}
                            index={index}
                            onUpdate={updateSection}
                            onDelete={deleteSection}
                          />
                        </div>
                      </div>

                      {/* Section Header with Expand/Collapse */}
                      <div className="px-4 pb-2 flex items-center justify-between">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleSection(section.id)}
                          className="flex items-center gap-2"
                        >
                          {expandedSections.has(section.id) ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                          <span>
                            {section.chapters.length} {section.chapters.length === 1 ? 'Chapter' : 'Chapters'}
                          </span>
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => addChapter(section.id)}>
                          <Plus className="w-4 h-4 mr-2" /> Add Chapter
                        </Button>
                      </div>

                      {/* Chapters List */}
                      {expandedSections.has(section.id) && section.chapters.length > 0 && (
                        <div className="px-4 pb-4 space-y-2">
                          {section.chapters.map((chapter) => (
                            <div
                              key={chapter.id}
                              className={`p-3 border rounded cursor-pointer transition-colors ${
                                activeChapter === chapter.id
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                              onClick={() => {
                                setActiveSection(section.id);
                                setActiveChapter(chapter.id);
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">{chapter.title || 'Untitled Chapter'}</p>
                                  {chapter.subtitle && <p className="text-sm text-gray-500">{chapter.subtitle}</p>}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteChapter(section.id, chapter.id);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Chapter Editor */}
      {activeData?.section && activeData?.chapter && (
        <Card>
          <CardHeader>
            <CardTitle>
              Editing: {activeData.section.title} - {activeData.chapter.title || 'Untitled Chapter'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="info">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info">Chapter Info</TabsTrigger>
                <TabsTrigger value="content">Notes & Content</TabsTrigger>
                <TabsTrigger value="questions">Questions</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4">
                <div>
                  <Label>Chapter Title</Label>
                  <Input
                    value={activeData.chapter.title}
                    onChange={(e) => updateChapter(activeData.section!.id, activeData.chapter!.id, 'title', e.target.value)}
                    placeholder="Chapter title"
                  />
                </div>

                <div>
                  <Label>Chapter Subtitle</Label>
                  <Input
                    value={activeData.chapter.subtitle}
                    onChange={(e) => updateChapter(activeData.section!.id, activeData.chapter!.id, 'subtitle', e.target.value)}
                    placeholder="Chapter subtitle"
                  />
                </div>

                <div>
                  <Label>Learning Objectives</Label>
                  <div className="space-y-2 mt-2">
                    {activeData.chapter.objectives.map((obj, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={obj}
                          onChange={(e) =>
                            updateChapterObjective(activeData.section!.id, activeData.chapter!.id, index, e.target.value)
                          }
                          placeholder={`Objective ${index + 1}`}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeChapterObjective(activeData.section!.id, activeData.chapter!.id, index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addChapterObjective(activeData.section!.id, activeData.chapter!.id)}
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Objective
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="content">
                <div>
                  <Label className="mb-2 block">Chapter Content</Label>
                  <TipTapEditor
                    content={activeData.chapter.content}
                    onChange={(content) => updateChapter(activeData.section!.id, activeData.chapter!.id, 'content', content)}
                    onMediaAdd={async (media) => {
                    if (!activeData?.chapter?.id || !activeData?.section?.id) return;

                    const chapterId = activeData.chapter.id;
                    const sectionId = activeData.section.id;

                    try {
                      let response;

                      // ✅ ALWAYS use FormData (important fix)
                      const formData = new FormData();

                      // FILE
                      if (media.file) {
                        formData.append('file', media.file);
                      }

                      // COMMON FIELDS (for ALL media types)
                      formData.append('type', media.type || 'IMAGE');
                      if (media.url) formData.append('url', media.url);
                      if (media.name) formData.append('name', media.name);
                      if (media.text) formData.append('text', media.text);

                      response = await fetch(`/api/courses/chapters/${chapterId}/media`, {
                        method: 'POST',
                        headers: {
                          Authorization: `Bearer ${localStorage.getItem('token')}`,
                        },
                        body: formData, // ✅ ALWAYS FormData
                      });

                      if (!response.ok) {
                        throw new Error('Upload failed');
                      }

                      const data = await response.json();

                      // ✅ SAFE STATE UPDATE (avoid stale activeData)
                      setFormData((prev) => ({
                        ...prev,
                        sections: prev.sections.map((section) =>
                          section.id === sectionId
                            ? {
                                ...section,
                                chapters: section.chapters.map((chapter) =>
                                  chapter.id === chapterId
                                    ? {
                                        ...chapter,
                                        media: [...(chapter.media || []), data.data],
                                      }
                                    : chapter
                                ),
                              }
                            : section
                        ),
                      }));

                    } catch (err) {
                      console.error('❌ Media upload failed', err);
                    }
                  }}
                  />
                </div>
              </TabsContent>

              <TabsContent value="questions" className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label>Quiz Questions</Label>
                  <Button size="sm" onClick={() => addQuestion(activeData.section!.id, activeData.chapter!.id)}>
                    <Plus className="w-4 h-4 mr-2" /> Add Question
                  </Button>
                </div>

                {activeData.chapter.questions.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No questions yet. Click "Add Question" to create a quiz.</p>
                ) : (
                  <div className="space-y-6">
                    {activeData.chapter.questions.map((question, qIndex) => (
                      <Card key={question.id}>
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base">Question {qIndex + 1}</CardTitle>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteQuestion(activeData.section!.id, activeData.chapter!.id, question.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <Textarea
                            value={question.question}
                            onChange={(e) =>
                              updateQuestion(activeData.section!.id, activeData.chapter!.id, question.id, 'question', e.target.value)
                            }
                            placeholder="Enter your question"
                            rows={2}
                          />

                          <div className="space-y-2">
                            <Label className="text-sm">Answers (check correct ones):</Label>
                            {question.answers.map((answer, aIndex) => (
                              <div key={aIndex} className="flex items-center gap-2">
                                <Input
                                  value={answer.text}
                                  onChange={(e) =>
                                    updateAnswer(
                                      activeData.section!.id,
                                      activeData.chapter!.id,
                                      question.id,
                                      aIndex,
                                      'text',
                                      e.target.value
                                    )
                                  }
                                  placeholder={`Answer ${aIndex + 1}`}
                                  className="flex-1"
                                />
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={answer.isCorrect}
                                    onChange={(e) =>
                                      updateAnswer(
                                        activeData.section!.id,
                                        activeData.chapter!.id,
                                        question.id,
                                        aIndex,
                                        'isCorrect',
                                        e.target.checked
                                      )
                                    }
                                    className="w-4 h-4"
                                  />
                                  <Label className="text-sm text-gray-600">Correct</Label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 sticky bottom-0 bg-white p-4 border-t border-gray-200">
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="outline"
          onClick={() => handleSave('DRAFT')}
          disabled={saving}
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Draft
        </Button>

        <Button
          onClick={() => handleSave('PUBLISHED')}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {course ? 'Update Course' : 'Publish Course'}
        </Button>
      </div>
    </div>
  );
}
